/**
 * The homepage's animated DNA helix, on a canvas. The owner asked for the old moving helix back on 2026-10-24
 * ("what ever happened to the helix thing"). It keeps what the old DnaHelix.tsx did (a turning double helix that leans
 * toward the mouse on desktop, a lighter version on phones, paused when it cannot be seen) and draws it in direction
 * B's colours: a blue strand, a violet strand, teal rungs, a soft glow round the front nodes. DnaHelix.tsx itself is
 * left as it was for the preview pages that still use it.
 *
 *  - "vertical" (from 1024px, HeroArt.tsx): the mockup's helix, drawn over the static SVG poster at the same place and
 *    scale (a 320 x 560 box fitted to the column's height, 2.1 turns, 27 nodes a strand). It fades in over the poster
 *    once its first frame is drawn, then turns, one full turn every 12 seconds, nodes in front larger and brighter.
 *  - "horizontal" (below 1024px): a slim band across the hero between the code line and the cheat sheet, six nodes a
 *    turn (38 nodes on a 375px phone, 44 on a 430px one) and 30 frames a second, so a phone does little work.
 *
 * Rules (docs/design/pages/home.md): the canvas fills a box whose size the markup reserves, so it never moves the page;
 * it starts only after the load event, when the browser is idle, so the headline stays the largest paint; it stops while
 * off screen or while the tab is hidden; under prefers-reduced-motion it draws one still frame; the device pixel ratio
 * is capped at 2. Colours come from .home-helix in index.css (--helix-a, --helix-b, --helix-rung), read again when the
 * theme changes.
 */
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type Orientation = "vertical" | "horizontal";
type Rgb = [number, number, number];
type Colors = { a: Rgb; b: Rgb; rung: Rgb };

/** One full turn every 12 seconds. */
const SPIN = (2 * Math.PI) / 12000;

function readColors(el: Element): Colors {
  const cs = getComputedStyle(el);
  const parse = (name: string, fallback: Rgb): Rgb => {
    const parts = cs.getPropertyValue(name).trim().split(/\s+/).map(Number);
    return parts.length === 3 && parts.every((n) => Number.isFinite(n)) ? (parts as Rgb) : fallback;
  };
  return { a: parse("--helix-a", [37, 99, 235]), b: parse("--helix-b", [139, 92, 246]), rung: parse("--helix-rung", [20, 184, 166]) };
}

const rgba = ([r, g, b]: Rgb, alpha: number) => `rgba(${r},${g},${b},${alpha.toFixed(3)})`;

type Node = { x: number; y: number; z: number };

/**
 * Draws one frame. `phase` is the turn so far. Both shapes are the same helix: positions along the axis u from 0 to 1,
 * angle = 2 pi turns u + phase (+ pi for the second strand), the offset from the axis amp sin(angle), the depth cos(angle).
 */
function drawFrame(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  phase: number,
  colors: Colors,
  orientation: Orientation,
  lean: { x: number; y: number },
) {
  ctx.clearRect(0, 0, w, h);
  let turns: number, steps: number, amp: number, stroke: number, dot: number, rungW: number;
  let place: (u: number, off: number) => { x: number; y: number };
  if (orientation === "vertical") {
    // The poster's geometry: a 320 x 560 box, scaled to the height and centred, the helix from y 20 to 540.
    const s = h / 560;
    const left = (w - 320 * s) / 2 + lean.x * 12;
    const top = lean.y * 8;
    turns = 2.1;
    steps = 26;
    amp = 92 * s;
    stroke = 5 * s;
    dot = 6.5 * s;
    rungW = 2.5 * s;
    place = (u, off) => ({ x: left + 160 * s + off, y: top + (20 + u * 520) * s });
  } else {
    const pad = 10;
    const len = Math.max(w - pad * 2, 1);
    // Six nodes a turn: a 375px phone gets 3 turns and 19 nodes a strand (38 nodes), a 430px phone 3.5 turns (44).
    turns = Math.max(1, Math.round((len / 115) * 2) / 2);
    steps = Math.min(36, Math.round(turns * 6));
    amp = h * 0.3;
    stroke = 2.5;
    dot = 3.6;
    rungW = 1.25;
    place = (u, off) => ({ x: pad + u * len, y: h / 2 + off });
  }

  const nodes = (strand: number): Node[] => {
    const out: Node[] = [];
    for (let i = 0; i <= steps; i++) {
      const u = i / steps;
      const angle = 2 * Math.PI * turns * u + phase + strand * Math.PI;
      const p = place(u, amp * Math.sin(angle));
      out.push({ x: p.x, y: p.y, z: Math.cos(angle) });
    }
    return out;
  };
  const strands = [nodes(0), nodes(1)];
  const tone = [colors.a, colors.b];

  // Rungs first, behind both strands; brighter where the helix faces the viewer, as in the mockup (0.35 to 0.8).
  ctx.lineCap = "round";
  ctx.lineWidth = rungW;
  for (let i = 0; i <= steps; i++) {
    const p = strands[0][i];
    const q = strands[1][i];
    ctx.strokeStyle = rgba(colors.rung, 0.35 + 0.45 * ((p.z + 1) / 2));
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(q.x, q.y);
    ctx.stroke();
  }

  // The strands, finely sampled, split into the half that faces away (drawn first, fainter) and the half in front.
  const samples = steps * 4;
  const strandPath = (strand: number, front: boolean) => {
    const path = new Path2D();
    let open = false;
    for (let j = 0; j <= samples; j++) {
      const u = j / samples;
      const angle = 2 * Math.PI * turns * u + phase + strand * Math.PI;
      const inFront = Math.cos(angle) >= 0;
      const p = place(u, amp * Math.sin(angle));
      if (inFront === front) {
        if (open) path.lineTo(p.x, p.y);
        else path.moveTo(p.x, p.y);
        open = true;
      } else if (open) {
        path.lineTo(p.x, p.y);
        open = false;
      }
    }
    return path;
  };
  const dots = (front: boolean) => {
    for (let strand = 0; strand < 2; strand++) {
      for (const n of strands[strand]) {
        if (n.z >= 0 !== front) continue;
        const r = dot * (0.85 + 0.15 * n.z);
        if (front) {
          // The soft glow: a wider, faint disc behind each front node.
          ctx.fillStyle = rgba(tone[strand], 0.16 * n.z);
          ctx.beginPath();
          ctx.arc(n.x, n.y, r * 2.2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = rgba(tone[strand], front ? 1 : 0.55 + 0.35 * (n.z + 1));
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  };
  ctx.lineWidth = stroke;
  for (const front of [false, true]) {
    for (let strand = 0; strand < 2; strand++) {
      ctx.strokeStyle = rgba(tone[strand], front ? 1 : 0.6);
      ctx.stroke(strandPath(strand, front));
    }
    dots(front);
  }
}

export default function HelixCanvas({
  orientation,
  className,
  onReady,
}: {
  orientation: Orientation;
  className?: string;
  /** Called once the first frame is on the canvas (HeroArt fades the static poster out then). */
  onReady?: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [shown, setShown] = useState(false);
  const readyRef = useRef(onReady);
  readyRef.current = onReady;

  useEffect(() => {
    const canvas = canvasRef.current;
    const box = canvas?.parentElement;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !box || !ctx) return;

    const still = !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const minFrameMs = orientation === "horizontal" ? 1000 / 30 : 0;
    let colors = readColors(canvas);
    let w = 0;
    let h = 0;
    let raf = 0;
    let idle = 0;
    let started = false;
    let onScreen = true;
    let phase = 0;
    let last = 0;
    let lastDrawn = 0;
    const lean = { x: 0, y: 0 };
    const target = { x: 0, y: 0 };

    const size = () => {
      const rect = box.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = rect.width;
      h = rect.height;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const paint = () => {
      if (w > 0 && h > 0) drawFrame(ctx, w, h, phase, colors, orientation, lean);
    };
    const running = () => started && !still && onScreen && !document.hidden;
    const tick = (now: number) => {
      raf = 0;
      if (!running()) return;
      raf = requestAnimationFrame(tick);
      const dt = last ? Math.min(now - last, 100) : 16;
      last = now;
      phase += dt * SPIN;
      // Ease the lean toward the mouse, so the helix follows it without jumping.
      lean.x += (target.x - lean.x) * 0.06;
      lean.y += (target.y - lean.y) * 0.06;
      if (minFrameMs && now - lastDrawn < minFrameMs) return;
      lastDrawn = now;
      paint();
    };
    const resume = () => {
      if (raf || !running()) return;
      last = 0;
      raf = requestAnimationFrame(tick);
    };
    const begin = () => {
      started = true;
      size();
      paint();
      setShown(true);
      readyRef.current?.();
      resume();
    };

    // Only after the page has loaded and the browser is idle: the headline is already painted by then.
    const schedule = () => {
      const ric = window.requestIdleCallback;
      if (ric) idle = ric(begin, { timeout: 1500 });
      else idle = window.setTimeout(begin, 300);
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });

    const resizeObserver = new ResizeObserver(() => {
      if (!started) return;
      size();
      paint();
    });
    resizeObserver.observe(box);
    const io = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      resume();
    });
    io.observe(box);
    const onVisibility = () => resume();
    document.addEventListener("visibilitychange", onVisibility);
    // The theme toggle swaps .light and .dark on <html>: read the colours again and redraw.
    const themeObserver = new MutationObserver(() => {
      colors = readColors(canvas);
      if (started) paint();
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    const onMouse = (e: MouseEvent) => {
      target.x = Math.max(-1, Math.min(1, (e.clientX / window.innerWidth - 0.5) * 2));
      target.y = Math.max(-1, Math.min(1, (e.clientY / window.innerHeight - 0.5) * 2));
    };
    const lean2Mouse = orientation === "vertical" && !still;
    if (lean2Mouse) window.addEventListener("mousemove", onMouse, { passive: true });

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (idle) {
        if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
        window.clearTimeout(idle);
      }
      window.removeEventListener("load", schedule);
      resizeObserver.disconnect();
      io.disconnect();
      themeObserver.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      if (lean2Mouse) window.removeEventListener("mousemove", onMouse);
    };
  }, [orientation]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={cn("home-helix pointer-events-none absolute inset-0 h-full w-full transition-opacity duration-500", shown ? "opacity-100" : "opacity-0", className)}
    />
  );
}
